/**
 * Plotea invitation link (`/invite?token=…`, docs/architecture/plotea.md
 * "Identity and roster"): shows the organization and the masked invited
 * address, then lets the visitor join by creating an account (the address
 * comes from the invitation), a sign-in link, Google/Microsoft, or by
 * signing in to an existing account. A signed-in visitor joins directly.
 * Nothing here says whether the address already has an account.
 *
 * Also `/reset-password?token=…` and the pending-invitation acceptance that
 * runs once a session exists.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AuthShell, NoticeBanner } from '../components/Auth'
import { BrandHeader } from '../components/Logo'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { useAuth } from '../lib/auth'
import { refresh } from '../lib/endpoints/auth'
import { setActiveOrganization } from '../lib/endpoints/organizations'
import {
  getPloteaProviders,
  ploteaApiError,
  resetPassword,
  startPloteaOAuth,
  type PloteaSocialProvider,
} from '../lib/endpoints/ploteaAuth'
import {
  acceptInvitationToken,
  invitationMagicLink,
  keepPendingInvitation,
  lookupInvitation,
  registerWithInvitation,
  ROLE_LABEL,
  takePendingInvitation,
} from '../lib/endpoints/ploteaOrganization'
import { ApiError } from '../lib/errors'
import { qk } from '../lib/queryKeys'

function tokenFromLocation(): string {
  return typeof window === 'undefined'
    ? ''
    : (new URLSearchParams(window.location.search).get('token') ?? '')
}

function message(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.serverCode === 'email_already_exists')
      return 'Konto z tym adresem już istnieje. Zaloguj się, aby dołączyć do organizacji.'
    return ploteaApiError(error.serverCode) ?? fallback
  }
  return fallback
}

/** Accepts by token, makes the organization active and refreshes `/me`. */
async function joinWithToken(token: string): Promise<void> {
  const { organization_id } = await acceptInvitationToken(token)
  await setActiveOrganization(organization_id)
  await refresh()
}

type Mode = 'choose' | 'register'

export function PloteaInvitePage() {
  const token = tokenFromLocation()
  const auth = useAuth()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [mode, setMode] = useState<Mode>('choose')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const invitation = useQuery({
    queryKey: ['plotea', 'invitation', token],
    queryFn: () => lookupInvitation(token),
    enabled: token !== '',
    retry: false,
    staleTime: Infinity,
  })
  const providers = useQuery({
    queryKey: ['plotea', 'auth-providers'],
    queryFn: getPloteaProviders,
    retry: 2,
  })
  const google = providers.data?.social.includes('google') ?? false
  const magicLink = providers.data?.magic_link ?? false

  const run = async (action: () => Promise<void>, fallback: string) => {
    setError(null)
    setNotice(null)
    setLoading(true)
    try {
      await action()
    } catch (e) {
      setError(message(e, fallback))
    } finally {
      setLoading(false)
    }
  }

  const join = () =>
    run(async () => {
      await joinWithToken(token)
      await qc.invalidateQueries({ queryKey: qk.me() })
      await qc.invalidateQueries({ queryKey: qk.organizations() })
      await nav({ to: '/site', replace: true })
    }, 'Nie udało się dołączyć. Zaproszenie mogło wygasnąć lub zostać już użyte.')

  const onOAuth = (provider: PloteaSocialProvider) =>
    run(async () => {
      const { redirect_url } = await startPloteaOAuth(provider, '/site', token)
      window.location.assign(redirect_url)
    }, 'Nie udało się rozpocząć logowania')

  const onRegister = () =>
    run(async () => {
      await registerWithInvitation(token, name, password)
      setMode('choose')
      setNotice(
        'Konto utworzone i dołączone do organizacji. Sprawdź skrzynkę i potwierdź adres email, a potem zaloguj się.',
      )
    }, 'Nie udało się utworzyć konta')

  const onMagicLink = () =>
    run(async () => {
      keepPendingInvitation(token)
      await invitationMagicLink(token)
      setNotice(
        `Wysłaliśmy link do logowania na ${invitation.data?.email ?? 'zaproszony adres'}.`,
      )
    }, 'Nie udało się wysłać linku')

  const onSignIn = () => {
    keepPendingInvitation(token)
    void nav({ to: '/login', replace: false })
  }

  const unavailable = token === '' || invitation.isError
  const signedIn = auth.status === 'authenticated'

  return (
    <AuthShell>
      <div className="mb-6">
        <BrandHeader topic="Zaproszenie" compact metadata={null} />
      </div>
      {unavailable ? (
        <div data-testid="invite-unavailable">
          <NoticeBanner
            message="Zaproszenie wygasło, zostało anulowane lub już użyte. Poproś o nowe."
            tone="error"
          />
          <Button
            className="w-full"
            variant="outline"
            onClick={() => void nav({ to: '/login', replace: true })}
          >
            Przejdź do logowania
          </Button>
        </div>
      ) : !invitation.data ? (
        <p className="text-sm text-muted-foreground">
          Wczytywanie zaproszenia…
        </p>
      ) : (
        <>
          <h1
            className="text-xl font-semibold text-foreground"
            data-testid="invite-heading"
          >
            Dołącz do organizacji {invitation.data.organization_name}
          </h1>
          <p className="mt-2 mb-5 text-sm text-muted-foreground">
            Zaproszenie dla {invitation.data.email} · rola{' '}
            {ROLE_LABEL[invitation.data.role].toLowerCase()}
          </p>
          {notice && <NoticeBanner message={notice} tone="success" centered />}
          {error && <NoticeBanner message={error} tone="error" />}

          {signedIn ? (
            <Button
              className="w-full"
              size="lg"
              data-testid="invite-join"
              disabled={loading}
              onClick={() => void join()}
            >
              Dołącz do organizacji
            </Button>
          ) : mode === 'register' ? (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault()
                void onRegister()
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="invite-name">Imię i nazwisko</Label>
                <Input
                  id="invite-name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invite-password">Hasło</Label>
                <Input
                  id="invite-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Min. 8 znaków"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <Button
                className="w-full"
                size="lg"
                type="submit"
                data-testid="invite-register-submit"
                disabled={loading || name.trim() === '' || password === ''}
              >
                Utwórz konto i dołącz
              </Button>
              <Button
                variant="link"
                className="h-auto w-full p-0"
                type="button"
                onClick={() => setMode('choose')}
              >
                Wróć
              </Button>
            </form>
          ) : (
            <div className="space-y-2">
              <Button
                className="w-full"
                size="lg"
                data-testid="invite-register"
                disabled={loading}
                onClick={() => setMode('register')}
              >
                Utwórz konto z hasłem
              </Button>
              <Button
                variant="outline"
                className="w-full"
                size="lg"
                data-testid="invite-oauth-microsoft"
                disabled={loading}
                onClick={() => void onOAuth('microsoft')}
              >
                Kontynuuj z Microsoft
              </Button>
              {google && (
                <Button
                  variant="outline"
                  className="w-full"
                  size="lg"
                  data-testid="invite-oauth-google"
                  disabled={loading}
                  onClick={() => void onOAuth('google')}
                >
                  Kontynuuj z Google
                </Button>
              )}
              {magicLink && (
                <Button
                  variant="outline"
                  className="w-full"
                  size="lg"
                  data-testid="invite-magic-link"
                  disabled={loading}
                  onClick={() => void onMagicLink()}
                >
                  Wyślij link do logowania
                </Button>
              )}
              <p className="pt-3 text-center text-sm text-muted-foreground">
                Masz już konto Plotea?{' '}
                <Button
                  variant="link"
                  className="h-auto p-0"
                  data-testid="invite-sign-in"
                  onClick={onSignIn}
                >
                  Zaloguj się
                </Button>
              </p>
            </div>
          )}
        </>
      )}
    </AuthShell>
  )
}

/**
 * Accepts an invitation kept by `/invite` once a session exists (password
 * sign-in, magic link). Errors are dropped: an used or foreign token simply
 * leaves "my invitations" as the way in.
 */
export function usePloteaPendingInvitation(enabled: boolean): void {
  const qc = useQueryClient()
  const done = useRef(false)
  useEffect(() => {
    if (!enabled || done.current) return
    done.current = true
    const token = takePendingInvitation()
    if (!token) return
    void joinWithToken(token)
      .then(() => {
        void qc.invalidateQueries({ queryKey: qk.me() })
        void qc.invalidateQueries({ queryKey: qk.organizations() })
        void qc.invalidateQueries({ queryKey: qk.myInvitations() })
      })
      .catch(() => undefined)
  }, [enabled, qc])
}

/** `/reset-password?token=…` from the reset email. */
export function PloteaResetPasswordPage() {
  const token = tokenFromLocation()
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const submit = async () => {
    setError(null)
    if (password.length < 8) {
      setError('Hasło musi mieć co najmniej 8 znaków.')
      return
    }
    if (password !== repeat) {
      setError('Hasła nie są takie same.')
      return
    }
    setLoading(true)
    try {
      await resetPassword(token, password)
      window.location.assign('/login?reset=true')
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 422
          ? 'Hasło nie spełnia wymagań.'
          : 'Link wygasł lub został już użyty. Poproś o nowy.',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell>
      <div className="mb-6">
        <BrandHeader topic="Analiza działek" compact metadata={null} />
      </div>
      <h1 className="mb-6 text-xl font-semibold text-foreground">
        Ustaw nowe hasło
      </h1>
      {token === '' ? (
        <NoticeBanner
          message="Link do zmiany hasła jest niepełny."
          tone="error"
        />
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          {error && <NoticeBanner message={error} tone="error" />}
          <div className="space-y-1.5">
            <Label htmlFor="reset-password">Nowe hasło</Label>
            <Input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reset-password-repeat">Powtórz hasło</Label>
            <Input
              id="reset-password-repeat"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          </div>
          <Button
            className="w-full"
            size="lg"
            type="submit"
            data-testid="reset-submit"
            disabled={loading}
          >
            Zapisz hasło
          </Button>
        </form>
      )}
    </AuthShell>
  )
}
